import { unwrap } from "@sagot/api-client";
import { useTranslation } from "@sagot/i18n";
import { Badge, Button, Dialog, DialogContent, ErrorState, Icon, Skeleton, toast } from "@sagot/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useErrorMessage } from "../../../../lib/api-errors";
import { useApi } from "../../../../session/session-provider";
import { useWorkspaceId } from "../../../../session/workspace-context";
import { wsKeys } from "../../shared";
import { describeDays, isShorter, requestBody, type ClassChange, type ClassState, type Selection } from "./retention-presets-model";

const K = "privacyM29.retention";

function DaysText({ days }: { days: number }) {
  const { t } = useTranslation();
  const parts = describeDays(days);
  return <>{t(`${K}.${parts.key}`, { count: parts.count })}</>;
}

function ChangeRow({ change }: { change: ClassChange }) {
  const { t } = useTranslation();
  const holdFlipped = change.hold_from !== change.hold_to;
  return (
    <li className="space-y-1 px-5 py-3" data-testid={`retention-change-${change.class}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm font-semibold">{t(`${K}.classes.${change.class}.name`)}</span>
        {change.from !== change.to ? (
          <span className="flex flex-wrap items-center gap-1.5 text-sm tabular-nums">
            <span className="text-fg-muted">
              <DaysText days={change.from} />
            </span>
            <Icon name="arrow-right" className="size-3.5 text-fg-subtle" />
            <strong className="font-semibold">
              <DaysText days={change.to} />
            </strong>
          </span>
        ) : null}
      </div>
      {change.from !== change.to ? (
        <p className={isShorter(change) ? "text-xs text-warning" : "text-xs text-fg-muted"}>{isShorter(change) ? t(`${K}.dialog.shorter`) : t(`${K}.dialog.longer`)}</p>
      ) : null}
      {holdFlipped ? (
        <Badge tone={change.hold_to ? "brand" : "neutral"}>
          <Icon name="lock" className="size-3" />
          {change.hold_to ? t(`${K}.dialog.holdOn`) : t(`${K}.dialog.holdOff`)}
        </Badge>
      ) : null}
    </li>
  );
}

interface ReviewProps {
  classes: readonly ClassState[];
  selection: Selection;
  onClose: () => void;
}

/** The dry run of the selection: what changes, which way, then Apply (audited `retention.preset_applied`). */
export function RetentionReviewDialog({ classes, selection, onClose }: ReviewProps) {
  const { t } = useTranslation();
  const api = useApi();
  const wsId = useWorkspaceId();
  const message = useErrorMessage();
  const queryClient = useQueryClient();
  const preview = useQuery({
    queryKey: ["ws", wsId, "settings", "retention-review", JSON.stringify(requestBody(classes, selection, true))] as const,
    queryFn: () => unwrap(api.PUT("/v1/retention-presets", { body: requestBody(classes, selection, true) })),
    gcTime: 0,
    retry: false,
  });
  const apply = useMutation({
    mutationFn: () => unwrap(api.PUT("/v1/retention-presets", { body: requestBody(classes, selection, false) })),
    onSuccess: async () => {
      toast.success(t(`${K}.applied`));
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["ws", wsId, "settings", "retention-presets"] }),
        queryClient.invalidateQueries({ queryKey: wsKeys.privacy(wsId) }),
        queryClient.invalidateQueries({ queryKey: ["ws", wsId, "settings", "audit"] }),
      ]);
      onClose();
    },
    onError: (caught) => toast.error(message(caught)),
  });
  const diff = preview.data?.diff ?? [];
  const body = (() => {
    if (preview.isPending) {
      return (
        <div className="space-y-3" role="status" aria-label={t("common.loading")}>
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      );
    }
    if (preview.isError) {
      return <ErrorState headingLevel={3} title={t(`${K}.dialog.failed`)} description={message(preview.error)} onRetry={() => void preview.refetch()} retryLabel={t("common.retry")} />;
    }
    if (diff.length === 0) return <p className="text-sm text-fg-muted">{t(`${K}.dialog.empty`)}</p>;
    return (
      <ul className="-mx-5 divide-y divide-line border-y border-line" aria-label={t(`${K}.dialog.colChange`)}>
        {diff.map((change) => (
          <ChangeRow key={change.class} change={change} />
        ))}
      </ul>
    );
  })();
  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent
        title={t(`${K}.dialog.title`)}
        closeLabel={t("common.close")}
        footer={
          <>
            <Button variant="secondary" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button loading={apply.isPending} disabled={!preview.data || diff.length === 0} onClick={() => { apply.mutate(); }}>
              {t(`${K}.dialog.confirm`)}
            </Button>
          </>
        }
      >
        <div className="space-y-3 text-sm">
          <p className="text-fg-muted">{t(`${K}.dialog.intro`)}</p>
          {body}
        </div>
      </DialogContent>
    </Dialog>
  );
}
